/**
 * 下載批改結果工具模組（Word 版）
 * 透過樣板 docx 產生符合格式的批改結果
 */

import JSZip from "jszip";

// 樣板檔案路徑
const TEMPLATE_PATH = "/templates/feedback-template.docx";
// docx 的 MIME type：不指定時 JSZip 預設 application/zip，行動裝置會依此把檔案存成 .zip
const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// 樣板表格佈局索引（根據範本結構）
const TEMPLATE_LAYOUT = {
  headerTable: 0,        // 座號、姓名、作文總分
  essayTitleTable: 1,    // 作文題目
  studentEssayTable: 2,  // 學生作文
  categoriesTable: 3,    // 各項分數
  overallTable: 4,       // 整體評語
  suggestionsTable: 5,   // 寫作建議
};

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

// 文字正規化
const normalizeText = (text: string | number | null | undefined = ""): string => {
  const normalized = String(text ?? "").trim();
  if (!normalized || normalized === "null" || normalized === "undefined") {
    return "";
  }
  return normalized;
};

// 清理 Markdown 語法
const stripMarkdown = (text: string): string => {
  return text
    // 移除粗體 **text** 或 __text__
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    // 移除斜體 *text* 或 _text_
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    // 移除行內程式碼 `code`
    .replace(/`([^`]+)`/g, "$1")
    // 移除連結 [text](url)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // 移除標題 # ## ###
    .replace(/^#{1,6}\s+/gm, "")
    .trim();
};

// XML 解析與序列化
const getXmlDoc = (xmlText: string): Document =>
  new window.DOMParser().parseFromString(xmlText, "application/xml");

const serializeXml = (doc: Document): string =>
  new window.XMLSerializer().serializeToString(doc);

// DOM 操作工具
const getTables = (doc: Document): Element[] =>
  Array.from(doc.getElementsByTagNameNS(W_NS, "tbl"));

const getTableRows = (table: Element): Element[] =>
  Array.from(table.childNodes).filter(
    (node): node is Element => node.nodeType === 1 && (node as Element).localName === "tr"
  );

const getRowCells = (row: Element): Element[] =>
  Array.from(row.childNodes).filter(
    (node): node is Element => node.nodeType === 1 && (node as Element).localName === "tc"
  );

const getCellParagraphs = (cell: Element): Element[] =>
  Array.from(cell.childNodes).filter(
    (node): node is Element => node.nodeType === 1 && (node as Element).localName === "p"
  );

const getParagraphText = (paragraph: Element): string => {
  const texts = Array.from(paragraph.getElementsByTagNameNS(W_NS, "t"));
  return texts.map((node) => node.textContent || "").join("");
};

const clearParagraphRuns = (paragraph: Element): void => {
  const runs = Array.from(paragraph.childNodes).filter(
    (node): node is Element => node.nodeType === 1 && (node as Element).localName === "r"
  );
  runs.forEach((node) => paragraph.removeChild(node));
};

const getRunTemplate = (paragraph: Element): Element | null => {
  const runs = Array.from(paragraph.childNodes).filter(
    (node): node is Element => node.nodeType === 1 && (node as Element).localName === "r"
  );
  return runs.length ? (runs[0].cloneNode(true) as Element) : null;
};

const createTextRun = (doc: Document, runTemplate: Element | null, text: string): Element => {
  const run = runTemplate
    ? (runTemplate.cloneNode(true) as Element)
    : doc.createElementNS(W_NS, "w:r");

  // 移除舊的文字節點
  Array.from(run.childNodes).forEach((node) => {
    if (node.nodeType === 1 && (node as Element).localName === "t") {
      run.removeChild(node);
    }
  });

  const textNode = doc.createElementNS(W_NS, "w:t");
  if (/^\s|\s$/.test(text)) {
    textNode.setAttributeNS("http://www.w3.org/XML/1998/namespace", "xml:space", "preserve");
  }
  textNode.textContent = text;
  run.appendChild(textNode);
  return run;
};

const setParagraphText = (doc: Document, paragraph: Element, text: string): void => {
  const runTemplate = getRunTemplate(paragraph);
  clearParagraphRuns(paragraph);
  paragraph.appendChild(createTextRun(doc, runTemplate, text || " "));
};

const setRunText = (doc: Document, run: Element, text: string): void => {
  if (!run) return;
  Array.from(run.childNodes).forEach((node) => {
    if (node.nodeType === 1 && (node as Element).localName === "t") {
      run.removeChild(node);
    }
  });
  const textNode = doc.createElementNS(W_NS, "w:t");
  if (/^\s|\s$/.test(text)) {
    textNode.setAttributeNS("http://www.w3.org/XML/1998/namespace", "xml:space", "preserve");
  }
  textNode.textContent = text;
  run.appendChild(textNode);
};

const replaceCellParagraphs = (cell: Element, paragraphs: Element[]): void => {
  Array.from(cell.childNodes).forEach((node) => {
    if (node.nodeType === 1 && (node as Element).localName === "p") {
      cell.removeChild(node);
    }
  });
  paragraphs.forEach((paragraph) => cell.appendChild(paragraph));
};

const setCellParagraphs = (
  doc: Document,
  cell: Element,
  lines: string[],
  templateParagraph?: Element
): void => {
  const baseParagraph = templateParagraph || getCellParagraphs(cell)[0];
  if (!baseParagraph) return;

  const runTemplate = getRunTemplate(baseParagraph);
  const paragraphs = lines.length ? lines : [""];
  const newParagraphs = paragraphs.map((line) => {
    const paragraph = baseParagraph.cloneNode(true) as Element;
    clearParagraphRuns(paragraph);
    paragraph.appendChild(createTextRun(doc, runTemplate, line || " "));
    return paragraph;
  });
  replaceCellParagraphs(cell, newParagraphs);
};

// 設定表頭值（座號、姓名、總分）
const setHeaderValues = (
  doc: Document,
  table: Element,
  seatNumber: string,
  studentName: string,
  totalScore: string
): void => {
  const rows = getTableRows(table);
  const headerRow = rows[0];
  if (!headerRow) return;
  const cells = getRowCells(headerRow);

  for (const cell of cells) {
    const cellParagraphs = getCellParagraphs(cell);
    if (!cellParagraphs.length) continue;
    const text = getParagraphText(cellParagraphs[0]);

    // 取得所有 runs
    const runs = Array.from(cellParagraphs[0].childNodes).filter(
      (node): node is Element => node.nodeType === 1 && (node as Element).localName === "r"
    );

    if (text.includes("座號")) {
      // 座號：最後一個 run 是數值
      if (runs.length > 1) {
        setRunText(doc, runs[runs.length - 1], seatNumber);
      } else if (runs.length === 1) {
        // 只有一個 run，直接設定整個文字
        setParagraphText(doc, cellParagraphs[0], `座號：${seatNumber}`);
      }
    } else if (text.includes("姓名")) {
      // 姓名：最後一個 run 是數值
      if (runs.length > 1) {
        setRunText(doc, runs[runs.length - 1], studentName);
      } else if (runs.length === 1) {
        setParagraphText(doc, cellParagraphs[0], `姓名：${studentName}`);
      }
    } else if (text.includes("作文總分")) {
      // 作文總分：找到數值的 run（通常是最後一個或倒數第二個）
      // 跳過包含"作文總分"和空白的 run
      const valueRun = runs.find((run) => {
        const runText = Array.from(run.getElementsByTagNameNS(W_NS, "t"))
          .map((node) => node.textContent || "")
          .join("");
        return runText.trim() && !runText.includes("作文總分") && !runText.includes("級分") && runText.trim() !== "";
      });
      if (valueRun) {
        setRunText(doc, valueRun, totalScore);
      } else if (runs.length > 0) {
        // 找不到就用最後一個
        setRunText(doc, runs[runs.length - 1], totalScore);
      }
    }
  }
};

// 類別數據介面
interface CategoryData {
  name: string;
  score: number;
  comment: string;
}

// 填入各項分數表格
const fillCategoriesTable = (
  doc: Document,
  table: Element,
  categories: CategoryData[]
): void => {
  const rows = getTableRows(table);

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const cells = getRowCells(row);
    if (cells.length < 2) continue;

    const category = categories[i];
    if (!category) continue;

    const name = normalizeText(category.name);
    const score = category.score != null ? String(category.score) : "";
    const comment = normalizeText(category.comment);

    // 左欄：名稱和分數
    const leftParagraphs = getCellParagraphs(cells[0]);
    if (leftParagraphs[0]) setParagraphText(doc, leftParagraphs[0], name);
    if (leftParagraphs[1]) setParagraphText(doc, leftParagraphs[1], score);

    // 右欄：評語
    const commentLines = comment.split(/\r?\n/).map((line) => line.trimEnd());
    setCellParagraphs(doc, cells[1], commentLines);
  }
};

// 填入整體評語表格
const fillOverallTable = (doc: Document, table: Element, overallComment: string): void => {
  const rows = getTableRows(table);
  if (!rows[0]) return;
  const cells = getRowCells(rows[0]);
  if (!cells[0]) return;

  const lines = overallComment.split(/\r?\n/).map((line) => line.trimEnd());
  setCellParagraphs(doc, cells[0], lines);
};

// 填入寫作建議表格
const fillSuggestionsTable = (
  doc: Document,
  table: Element,
  suggestions: string[]
): void => {
  const rows = getTableRows(table);
  if (!rows[0]) return;
  const cells = getRowCells(rows[0]);
  if (!cells[0]) return;

  const baseParagraph = getCellParagraphs(cells[0])[0];
  if (!baseParagraph) return;

  const runTemplate = getRunTemplate(baseParagraph);
  const newParagraphs: Element[] = [];

  suggestions.forEach((suggestion) => {
    const paragraph = baseParagraph.cloneNode(true) as Element;
    clearParagraphRuns(paragraph);
    // 清理 Markdown 語法，保留原始編號格式
    const cleanedSuggestion = stripMarkdown(suggestion);
    paragraph.appendChild(createTextRun(doc, runTemplate, cleanedSuggestion));
    newParagraphs.push(paragraph);
  });

  if (newParagraphs.length > 0) {
    replaceCellParagraphs(cells[0], newParagraphs);
  }
};

// 填入作文題目表格
const fillEssayTitleTable = (
  doc: Document,
  table: Element,
  essayTitle: string
): void => {
  const rows = getTableRows(table);
  if (!rows[0]) return;
  const cells = getRowCells(rows[0]);
  if (!cells[0]) return;

  const cellParagraphs = getCellParagraphs(cells[0]);
  if (cellParagraphs[0]) {
    setParagraphText(doc, cellParagraphs[0], essayTitle || " ");
  }
};

// 填入學生作文表格（文字版）
const fillStudentEssayTable = (
  doc: Document,
  table: Element,
  studentEssay: string
): void => {
  const rows = getTableRows(table);
  if (!rows[0]) return;
  const cells = getRowCells(rows[0]);
  if (!cells[0]) return;

  // 將學生作文按行分割
  const lines = studentEssay.split(/\r?\n/).map((line) => line.trimEnd());
  setCellParagraphs(doc, cells[0], lines.length > 0 ? lines : [" "]);
};

// Base64 轉 ArrayBuffer
const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  // 移除 data:image/xxx;base64, 前綴
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

// 生成圖片的 Drawing XML 字串（不包含 w:p，因為會用 DOM 插入）
const generateDrawingXml = (relId: string, picId: number, imageName: string, cx: number, cy: number): string => {
  return `<w:drawing xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
    <wp:inline distT="0" distB="0" distL="0" distR="0">
      <wp:extent cx="${cx}" cy="${cy}"/>
      <wp:effectExtent l="0" t="0" r="0" b="0"/>
      <wp:docPr id="${picId}" name="${imageName}"/>
      <wp:cNvGraphicFramePr>
        <a:graphicFrameLocks noChangeAspect="1"/>
      </wp:cNvGraphicFramePr>
      <a:graphic>
        <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
          <pic:pic>
            <pic:nvPicPr>
              <pic:cNvPr id="${picId}" name="${imageName}"/>
              <pic:cNvPicPr/>
            </pic:nvPicPr>
            <pic:blipFill>
              <a:blip r:embed="${relId}"/>
              <a:stretch>
                <a:fillRect/>
              </a:stretch>
            </pic:blipFill>
            <pic:spPr>
              <a:xfrm>
                <a:off x="0" y="0"/>
                <a:ext cx="${cx}" cy="${cy}"/>
              </a:xfrm>
              <a:prstGeom prst="rect">
                <a:avLst/>
              </a:prstGeom>
            </pic:spPr>
          </pic:pic>
        </a:graphicData>
      </a:graphic>
    </wp:inline>
  </w:drawing>`;
};

// 在表格中插入圖片（使用 base64 數據）
const insertImageInTable = async (
  zip: JSZip,
  doc: Document,
  table: Element,
  images: ImageData[]
): Promise<void> => {
  const rows = getTableRows(table);
  if (!rows[0]) return;
  const cells = getRowCells(rows[0]);
  if (!cells[0]) return;

  // 清空現有段落
  const existingParagraphs = getCellParagraphs(cells[0]);
  existingParagraphs.forEach((p) => cells[0].removeChild(p));

  // 取得或建立 relationships 檔案
  const relsPath = "word/_rels/document.xml.rels";
  const relsFile = zip.file(relsPath);
  let relsXml: string;
  if (relsFile) {
    relsXml = await relsFile.async("text");
  } else {
    relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`;
  }
  const relsDoc = getXmlDoc(relsXml);
  const relationships = relsDoc.documentElement;

  // 找出最大的現有 rId 數字
  const existingRels = relationships.getElementsByTagName("Relationship");
  let maxRelId = 0;
  for (let i = 0; i < existingRels.length; i++) {
    const id = existingRels[i].getAttribute("Id") || "";
    const match = id.match(/rId(\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxRelId) maxRelId = num;
    }
  }
  let relIdCounter = maxRelId + 1;

  // 處理 Content_Types
  const contentTypesFile = zip.file("[Content_Types].xml");
  const contentTypesXml = contentTypesFile ? await contentTypesFile.async("text") : "";
  const contentTypesDoc = getXmlDoc(contentTypesXml);
  const typesElement = contentTypesDoc.documentElement;

  for (let i = 0; i < images.length; i++) {
    const imageData = images[i];
    if (!imageData || !imageData.base64) continue;

    try {
      // 從 base64 轉換為 ArrayBuffer
      const arrayBuffer = base64ToArrayBuffer(imageData.base64);

      // 判斷圖片類型
      const mimeType = imageData.mimeType || "image/jpeg";
      let extension = "jpg";
      if (mimeType.includes("png")) extension = "png";
      else if (mimeType.includes("gif")) extension = "gif";
      else if (mimeType.includes("webp")) extension = "webp";

      const imageName = `image${relIdCounter}.${extension}`;
      const imagePath = `word/media/${imageName}`;

      // 將圖片加入 zip（圖片本身已是壓縮格式，再 DEFLATE 只花時間不省空間）
      zip.file(imagePath, arrayBuffer, { compression: "STORE" });

      // 添加 Content Type（如果不存在）
      const existingDefault = Array.from(typesElement.getElementsByTagName("Default")).find(
        (el) => el.getAttribute("Extension") === extension
      );
      if (!existingDefault) {
        // 使用正確的命名空間創建元素
        const ctNs = "http://schemas.openxmlformats.org/package/2006/content-types";
        const defaultElement = contentTypesDoc.createElementNS(ctNs, "Default");
        defaultElement.setAttribute("Extension", extension);
        defaultElement.setAttribute("ContentType", mimeType);
        typesElement.appendChild(defaultElement);
      }

      // 添加 relationship
      const relId = `rId${relIdCounter}`;
      const relElement = relsDoc.createElementNS(
        "http://schemas.openxmlformats.org/package/2006/relationships",
        "Relationship"
      );
      relElement.setAttribute("Id", relId);
      relElement.setAttribute("Type", "http://schemas.openxmlformats.org/officeDocument/2006/relationships/image");
      relElement.setAttribute("Target", `media/${imageName}`);
      relationships.appendChild(relElement);

      // 圖片尺寸計算（EMU單位，1英寸 = 914400 EMU，1cm = 360000 EMU）
      // 設定最大寬度約 15cm，最大高度約 20cm
      const maxWidthEmu = 5400000;  // 15cm
      const maxHeightEmu = 7200000; // 20cm

      // 取得圖片實際尺寸，計算適當的顯示尺寸
      const imgWidth = imageData.width || 800;
      const imgHeight = imageData.height || 600;
      const aspectRatio = imgWidth / imgHeight;

      let finalWidth: number;
      let finalHeight: number;

      // 按比例縮放，確保不超過最大尺寸
      if (aspectRatio > maxWidthEmu / maxHeightEmu) {
        // 圖片較寬，以寬度為準
        finalWidth = maxWidthEmu;
        finalHeight = Math.round(maxWidthEmu / aspectRatio);
      } else {
        // 圖片較高，以高度為準
        finalHeight = maxHeightEmu;
        finalWidth = Math.round(maxHeightEmu * aspectRatio);
      }

      // 創建段落和 run 元素
      const paragraph = doc.createElementNS(W_NS, "w:p");
      const run = doc.createElementNS(W_NS, "w:r");

      // 使用字串模板生成 drawing XML
      const drawingXmlStr = generateDrawingXml(relId, relIdCounter, imageName, finalWidth, finalHeight);
      const drawingXmlDoc = getXmlDoc(drawingXmlStr);
      const importedDrawing = doc.importNode(drawingXmlDoc.documentElement, true);

      run.appendChild(importedDrawing);
      paragraph.appendChild(run);
      cells[0].appendChild(paragraph);

      relIdCounter++;
    } catch (error) {
      console.error("插入圖片失敗:", error);
      // 如果圖片插入失敗，添加提示文字
      const paragraph = doc.createElementNS(W_NS, "w:p");
      const run = doc.createElementNS(W_NS, "w:r");
      const text = doc.createElementNS(W_NS, "w:t");
      text.textContent = `[圖片插入失敗]`;
      run.appendChild(text);
      paragraph.appendChild(run);
      cells[0].appendChild(paragraph);
    }
  }

  // 更新 relationships 檔案
  zip.file(relsPath, serializeXml(relsDoc));

  // 更新 Content_Types 檔案
  zip.file("[Content_Types].xml", serializeXml(contentTypesDoc));
};

// 觸發下載
const triggerDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // 行動裝置啟動下載較慢，立刻釋放會導致下載失敗或存出 0 byte 檔案
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// 載入範本
const loadTemplateZip = async (templatePath: string): Promise<JSZip> => {
  const response = await fetch(templatePath);
  if (!response.ok) {
    throw new Error("無法取得樣板檔案");
  }
  const buffer = await response.arrayBuffer();
  return JSZip.loadAsync(buffer);
};

// 圖片數據介面
interface ImageData {
  base64: string;      // base64 編碼的圖片數據（含 data:image/xxx;base64, 前綴）
  mimeType: string;    // 圖片 MIME 類型，如 image/png, image/jpeg
  width?: number;      // 圖片原始寬度（像素）
  height?: number;     // 圖片原始高度（像素）
}

// 評估數據介面
interface EvaluationData {
  totalScore?: number;
  categories?: CategoryData[];
  overallComment?: string;
  suggestions?: string[];
  essayTitle?: string;           // 作文題目
  studentEssay?: string;         // 學生作文（文字）
  studentImages?: ImageData[];   // 學生作文（圖片 base64 數據）
}

// 下載選項介面
interface DownloadOptions {
  seatNumber?: string;
  studentName?: string;
}

/**
 * 下載批改結果 Word 檔
 */
export const downloadCorrectionResult = async (
  evaluationData: EvaluationData,
  options: DownloadOptions = {},
  onStart?: () => void,
  onComplete?: () => void,
  onError?: (message: string) => void
): Promise<void> => {
  if (!evaluationData) {
    onError?.("沒有可下載的批改結果數據");
    return;
  }

  onStart?.();

  try {
    const blob = await buildCorrectionResultBlob(evaluationData, options);
    triggerDownload(blob, "作文批改結果.docx");
    onComplete?.();
  } catch (error) {
    const message = error instanceof Error ? error.message : "未知錯誤";
    // 失敗路徑不呼叫 onComplete，否則 download_word_completed 會連失敗也送出，
    // 導致下載成功率虛高
    onError?.(`下載準備過程發生錯誤: ${message}`);
  }
};

/**
 * 產生批改結果 Word 檔的 Blob(不觸發下載)— 供班級成績單 zip 批次打包使用
 */
export const buildCorrectionResultBlob = async (
  evaluationData: EvaluationData,
  options: DownloadOptions = {}
): Promise<Blob> => {
  {
    const seatNumber = normalizeText(options.seatNumber);
    const studentName = normalizeText(options.studentName);
    const totalScore = evaluationData.totalScore != null ? String(evaluationData.totalScore) : "";
    const categories = evaluationData.categories || [];
    const overallComment = normalizeText(evaluationData.overallComment);
    const suggestions = evaluationData.suggestions || [];
    const essayTitle = normalizeText(evaluationData.essayTitle);
    const studentEssay = normalizeText(evaluationData.studentEssay);
    const studentImages = evaluationData.studentImages || [];

    // 載入範本
    const zip = await loadTemplateZip(TEMPLATE_PATH);
    const documentXmlFile = zip.file("word/document.xml");
    if (!documentXmlFile) {
      throw new Error("範本格式錯誤");
    }
    const documentXml = await documentXmlFile.async("text");
    const doc = getXmlDoc(documentXml);
    const tables = getTables(doc);

    // 填入表頭（座號、姓名、總分）
    const headerTable = tables[TEMPLATE_LAYOUT.headerTable];
    if (headerTable) {
      setHeaderValues(doc, headerTable, seatNumber, studentName, totalScore);
    }

    // 填入作文題目
    const essayTitleTable = tables[TEMPLATE_LAYOUT.essayTitleTable];
    if (essayTitleTable && essayTitle) {
      fillEssayTitleTable(doc, essayTitleTable, essayTitle);
    }

    // 填入學生作文（圖片或文字）
    const studentEssayTable = tables[TEMPLATE_LAYOUT.studentEssayTable];
    if (studentEssayTable) {
      if (studentImages.length > 0) {
        // 有圖片，插入圖片
        await insertImageInTable(zip, doc, studentEssayTable, studentImages);
      } else if (studentEssay) {
        // 無圖片，填入文字
        fillStudentEssayTable(doc, studentEssayTable, studentEssay);
      }
    }

    // 填入各項分數
    const categoriesTable = tables[TEMPLATE_LAYOUT.categoriesTable];
    if (categoriesTable && categories.length > 0) {
      fillCategoriesTable(doc, categoriesTable, categories);
    }

    // 填入整體評語
    const overallTable = tables[TEMPLATE_LAYOUT.overallTable];
    if (overallTable && overallComment) {
      fillOverallTable(doc, overallTable, overallComment);
    }

    // 填入寫作建議
    const suggestionsTable = tables[TEMPLATE_LAYOUT.suggestionsTable];
    if (suggestionsTable && suggestions.length > 0) {
      fillSuggestionsTable(doc, suggestionsTable, suggestions);
    }

    // 更新並產出
    zip.file("word/document.xml", serializeXml(doc));
    // JSZip 預設不壓縮（STORE），docx 本身是 zip，要明確指定 DEFLATE 才會縮小；圖片部件各自設 STORE
    return zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      mimeType: DOCX_MIME_TYPE,
    });
  }
};

// 導出類型
export type { ImageData };

export default { downloadCorrectionResult };
